"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useConfigTranslation } from "@/features/settings/utils/configUtils";
import { JobHeader } from "./JobHeader";
import { CompanyDescription } from "./CompanyDescription";
import { JobDetails } from "./JobDetails";
import { JobLocationMap } from "./JobLocationMap";

interface JobProps {
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    working_hours: string;
    facility_type: string;
    description: string;
    requirements: string[];
    benefits: string[];
    created_at: string;
    company_id: string;
    is_active: boolean;
    salary_min?: number;
    salary_max?: number;
    contract_type?: string;
    company_description?: {
      de?: string;
      en?: string;
      es?: string;
    };
  };
  talentAppliedForJob?: boolean;
  applicationId?: string;
  loading?: boolean;
  onApply?: (jobId: string, companyId: string) => void;
}

export default function Job({
  job,
  talentAppliedForJob = false,
  applicationId,
  loading = false,
  onApply
}: JobProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = i18n.language;
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(
    new Set([job.company_id])
  );

  const {
    getFacilityTypeLabel,
    getWorkingHoursLabel,
    getContractTypeLabel,
    getBenefitLabel,
    getRequirementLabel
  } = useConfigTranslation();

  // Determine if apply button should be shown
  const showApplyButton = !talentAppliedForJob && !!onApply;
  const showAppliedButton = talentAppliedForJob;

  // Toggle company description expansion
  const toggleCompanyDescription = (companyId: string) => {
    setExpandedCompanies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const getCompanyDescription = (description: {
    de?: string;
    en?: string;
    es?: string;
  }) => {
    // Try current language first, then fallback to German, then English, then first available
    return (
      description[currentLanguage as keyof typeof description] ||
      description.de ||
      description.en ||
      description.es ||
      Object.values(description)[0]
    );
  };
  return (
    <Card className="hover:shadow-md transition-shadow">
      <JobHeader
        title={job.title}
        company={job.company}
        location={job.location}
        working_hours={job.working_hours}
        facility_type={job.facility_type}
        contract_type={job.contract_type}
        salary_min={job.salary_min}
        salary_max={job.salary_max}
        getFacilityTypeLabel={getFacilityTypeLabel}
        getWorkingHoursLabel={getWorkingHoursLabel}
        getContractTypeLabel={getContractTypeLabel}
      />
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            {/* Company Description Section */}
            <CompanyDescription
              companyId={job.company_id}
              companyDescription={job.company_description}
              expandedCompanies={expandedCompanies}
              toggleCompanyDescription={toggleCompanyDescription}
              getCompanyDescription={getCompanyDescription}
              aboutUsLabel={t("talent:jobs.about_us")}
            />
          </div>

          {job.location && (
            <div className="flex-1">
              <JobLocationMap location={job.location} />
            </div>
          )}
        </div>

        <JobDetails
          description={job.description}
          requirements={job.requirements}
          benefits={job.benefits}
          getRequirementLabel={getRequirementLabel}
          getBenefitLabel={getBenefitLabel}
          requirementsLabel={t("talent:jobs.details.requirements")}
          benefitsLabel={t("talent:jobs.details.benefits")}
        />

        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <p className="text-sm text-gray-500 order-2 md:order-1 text-center md:text-left">
            {t("talent:jobs.details.published_on")}{" "}
            {new Date(job.created_at).toLocaleDateString("de-DE")}
          </p>
          <div className="order-1 md:order-2 flex flex-col md:flex-row gap-2">
            {showApplyButton && (
              <Button
                onClick={() => onApply(job.id, job.company_id)}
                disabled={loading}
                variant="default"
                className="w-full md:w-auto"
              >
                {loading
                  ? t("common:status.loading")
                  : t("common:buttons.apply")}
              </Button>
            )}
            {showAppliedButton && (
              <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                <Button
                  disabled={true}
                  variant="secondary"
                  className="w-full md:w-auto"
                >
                  {t("talent:jobs.applied")}
                </Button>
                <Button
                  disabled={!applicationId}
                  variant={applicationId ? "link" : "secondary"}
                  onClick={() => navigate(`/your_application/${applicationId}`)}
                  className="w-full md:w-auto"
                >
                  {t("talent:jobs.see_application")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
